import React from 'react';

import i18n from 'util/i18n';

import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Text from 'components/Form/Text';
import Textarea from 'components/Form/Textarea';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import { CommonOnUpdate, CommonOnError, CommonRequired } from 'types/props';
import { SubstanceDrugsInvolvementItemModel } from 'forms/sections/substance/purchase/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import { criminalJusticeorPublicSafetyPosition } from 'constants/enums/substanceOptions';
import Dropdown from 'components/Form/Dropdown';
import DrugType from './DrugType';

interface Props
  extends Partial<SubstanceDrugsInvolvementItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  requireDrugWhileSafety?: boolean
  requireDrugWithClearance?: boolean
  requireDrugInFuture?: boolean
  scrollIntoView?: boolean
  formType?: string
}

export default class DrugInvolvement extends ValidationElement<Props> {
  update = (values: Partial<SubstanceDrugsInvolvementItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        DrugType: this.props.DrugType,
        FirstInvolvement: this.props.FirstInvolvement,
        RecentInvolvement: this.props.RecentInvolvement,
        NatureOfInvolvement: this.props.NatureOfInvolvement,
        Reasons: this.props.Reasons,
        InvolvementWhileEmployed: this.props.InvolvementWhileEmployed,
        InvolvementWithClearance: this.props.InvolvementWithClearance,
        InvolvementInFuture: this.props.InvolvementInFuture,
        Explanation: this.props.Explanation,
        Comments: this.props.Comments,
        InvolvementInFutureComments: this.props.InvolvementInFutureComments,
        DrugTypeOther: this.props.DrugTypeOther, // PVQ Only
        HowManyTimes: this.props.HowManyTimes, // PVQ Only
        InvolvementWhileEmployedPosition: this.props.InvolvementWhileEmployedPosition, // PVQ Only
        InvolvementWhileEmployedExplanation: this.props.InvolvementWhileEmployedExplanation, // PVQ Only
        InvolvementWithClearanceExplanation: this.props.InvolvementWithClearanceExplanation, // PVQ Only
        InvolvementInFutureExplanation: this.props.InvolvementInFutureExplanation, // PVQ Only
        ...values,
      });
    }
  };

  updateDrugType = (values: SubstanceDrugsInvolvementItemModel['DrugType']) => {
    this.update({ DrugType: values });
  };

  updateFirstInvolvement = (values: SubstanceDrugsInvolvementItemModel['FirstInvolvement']) => {
    this.update({ FirstInvolvement: values });
  };

  updateRecentInvolvement = (values: SubstanceDrugsInvolvementItemModel['RecentInvolvement']) => {
    this.update({ RecentInvolvement: values });
  };

  updateNatureOfInvolvement = (
    values: SubstanceDrugsInvolvementItemModel['NatureOfInvolvement']
  ) => {
    this.update({ NatureOfInvolvement: values });
  };

  updateReasons = (values: SubstanceDrugsInvolvementItemModel['Reasons']) => {
    this.update({ Reasons: values });
  };

  updateInvolvementWhileEmployed = (
    values: SubstanceDrugsInvolvementItemModel['InvolvementWhileEmployed']
  ) => {
    this.update({ InvolvementWhileEmployed: values });
  };

  updateInvolvementWithClearance = (
    values: SubstanceDrugsInvolvementItemModel['InvolvementWithClearance']
  ) => {
    this.update({ InvolvementWithClearance: values });
  };

  updateInvolvementInFuture = (
    values: SubstanceDrugsInvolvementItemModel['InvolvementInFuture']
  ) => {
    const isYes = values.value === 'Yes';
    this.update({
      InvolvementInFuture: values,
      Explanation: isYes ? this.props.Explanation : {},
      InvolvementInFutureComments: isYes ? this.props.InvolvementInFutureComments : {}
    });
  };

  updateExplanation = (values: SubstanceDrugsInvolvementItemModel['Explanation']) => {
    this.update({ Explanation: values });
  };

  updateComments = (values: SubstanceDrugsInvolvementItemModel['Comments']) => {
    this.update({ Comments: values });
  };

  updateInvolvementInFutureComments = (values: SubstanceDrugsInvolvementItemModel['InvolvementInFutureComments']) => {
    this.update({ InvolvementInFutureComments: values });
  };

  updateDrugTypeOther = (values: SubstanceDrugsInvolvementItemModel['DrugTypeOther']) => {
    this.update({ DrugTypeOther: values });
  };

  updateHowManyTimes = (values: SubstanceDrugsInvolvementItemModel['HowManyTimes']) => {
    this.update({ HowManyTimes: values });
  };

  updateInvolvementWhileEmployedPosition = (values: SubstanceDrugsInvolvementItemModel['InvolvementWhileEmployedPosition']) => {
    this.update({ InvolvementWhileEmployedPosition: values });
  };

  updateInvolvementWhileEmployedExplanation = (values: SubstanceDrugsInvolvementItemModel['InvolvementWhileEmployedExplanation']) => {
    this.update({ InvolvementWhileEmployedExplanation: values });
  };

  updateInvolvementWithClearanceExplanation = (values: SubstanceDrugsInvolvementItemModel['InvolvementWithClearanceExplanation']) => {
    this.update({ InvolvementWithClearanceExplanation: values });
  };

  updateInvolvementInFutureExplanation = (values: SubstanceDrugsInvolvementItemModel['InvolvementInFutureExplanation']) => {
    this.update({ InvolvementInFutureExplanation: values });
  };

  render() {
    const { requireDrugWhileSafety, requireDrugWithClearance, requireDrugInFuture } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-involvement">
        <Field
          title={i18n.t('substance.pvq.drugs.involvement.heading.drugType')}
          attribute={`${attribute}.DrugType`}
          className="drug-type-involvement"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DrugType
            {...this.props.DrugType}
            name="DrugType"
            attribute={attribute}
            formType={this.props.formType}
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>
        {this.props.DrugType?.value === 'Other' && (

          <Field
            title={i18n.t('substance.pvq.drugs.involvement.heading.otherDrugType')}
            attribute={`${attribute}.DrugTypeOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.DrugTypeOther}
              name="DrugTypeOther"
              attribute={`${attribute}.DrugTypeOther`}
              className="drugTypeOther"
              onUpdate={this.updateDrugTypeOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('substance.pvq.drugs.involvement.heading.firstInvolvement')}
          adjustFor="datecontrol"
          attribute={`${attribute}.FirstInvolvement`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="FirstInvolvement"
            attribute={`${attribute}.FirstInvolvement`}
            className="first-involvement"
            {...this.props.FirstInvolvement}
            hideDay
            minDateEqualTo
            showEstimated
            onUpdate={this.updateFirstInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.involvement.heading.recentInvolvement')}
          attribute={`${attribute}.RecentInvolvement`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.RecentInvolvement}
            name="RecentInvolvement"
            attribute={`${attribute}.RecentInvolvement`}
            className="recent-involvement"
            hideDay
            prefix="drugPurchase"
            minDate={this.props.FirstInvolvement}
            minDateEqualTo
            showEstimated
            onUpdate={this.updateRecentInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t('substance.pvq.drugs.involvement.heading.howManyTimes')}
          attribute={`${attribute}.HowManyTimes`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            {...this.props.HowManyTimes}
            name="HowManyTimes"
            attribute={`${attribute}.HowManyTimes`}
            className="howManyTimes"
            onUpdate={this.updateHowManyTimes}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.involvement.heading.reasons')}
          attribute={`${attribute}.Reasons`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Reasons"
            attribute={`${attribute}.Reasons`}
            className="reasons"
            {...this.props.Reasons}
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Branch
          name="InvolvementWhileEmployed"
          attribute={`${attribute}.InvolvementWhileEmployed`}
          label={i18n.t('substance.pvq.drugs.involvement.heading.involvementWhileEmployed')}
          labelSize="h4"
          className="involvement-while-employed"
          {...this.props.InvolvementWhileEmployed}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateInvolvementWhileEmployed}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.InvolvementWhileEmployed?.value === 'Yes' && (
          <>

            <Field
              title={i18n.t('substance.pvq.drugs.involvement.heading.involvementWhileEmployedPosition')}
              attribute={`${attribute}.InvolvementWhileEmployedPosition`}
              adjustFor="buttons"
              shrink
              className="no-margin-bottom"
            >
              <Dropdown
                attribute={`${attribute}.InvolvementWhileEmployedPosition`}
                name="InvolvementWhileEmployedPosition"
                className="involvementWhileEmployedPosition"
                onUpdate={this.updateInvolvementWhileEmployedPosition}
                onError={this.props.onError}
                value={this.props.InvolvementWhileEmployedPosition?.value}
                required={this.props.required}
              >
                {criminalJusticeorPublicSafetyPosition.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Dropdown>
            </Field>

            <Field
              title={i18n.t('substance.pvq.drugs.involvement.heading.involvementWhileEmployedExplanation')}
              attribute={`${attribute}.InvolvementWhileEmployedExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                {...this.props.InvolvementWhileEmployedExplanation}
                name="involvementWhileEmployedExplanation"
                attribute={`${attribute}.InvolvementWhileEmployedExplanation`}
                className="involvementWhileEmployedExplanation"
                onUpdate={this.updateInvolvementWhileEmployedExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </>
        )}
        <Branch
          name="InvolvementWithClearance"
          attribute={`${attribute}.InvolvementWithClearance`}
          label={i18n.t('substance.pvq.drugs.involvement.heading.involvementWithClearance')}
          labelSize="h4"
          className="involvement-with-clearance"
          {...this.props.InvolvementWithClearance}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateInvolvementWithClearance}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.InvolvementWithClearance?.value === 'Yes' && (
          <Field
            title={i18n.t('substance.pvq.drugs.involvement.heading.involvementWithClearanceExplanation')}
            attribute={`${attribute}.InvolvementWithClearanceExplanation`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.InvolvementWithClearanceExplanation}
              name="InvolvementWithClearanceExplanation"
              attribute={`${attribute}.InvolvementWithClearanceExplanation`}
              className="involvementWithClearanceExplanation"
              onUpdate={this.updateInvolvementWithClearanceExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Branch
          name="InvolvementInFuture"
          attribute={`${attribute}.InvolvementInFuture`}
          label={i18n.t('substance.pvq.drugs.involvement.heading.involvementInFuture')}
          labelSize="h4"
          className="involvement-in-future"
          {...this.props.InvolvementInFuture}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateInvolvementInFuture}
          scrollIntoView={this.props.scrollIntoView}
        />

        {
          (this.props.InvolvementInFuture?.value === 'Yes'
          || this.props.InvolvementInFuture?.value === 'No') && (
            <Field
              title={i18n.t('substance.pvq.drugs.involvement.heading.explanation')}
              attribute={`${attribute}.Explanation`}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                name="Explanation"
                attribute={`${attribute}.Explanation`}
                className="explanation"
                {...this.props.Explanation}
                onUpdate={this.updateExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          )
      }
      </div>
    ) : (
      <div className="drug-involvement">
        <Field
          title={i18n.t('substance.drugs.involvement.heading.drugType')}
          attribute={`${attribute}.DrugType`}
          className="drug-type-involvement"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DrugType
            name="DrugType"
            attribute={attribute}
            {...this.props.DrugType}
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.involvement.heading.firstInvolvement')}
          adjustFor="datecontrol"
          attribute={`${attribute}.FirstInvolvement`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="FirstInvolvement"
            attribute={`${attribute}.FirstInvolvement`}
            className="first-involvement"
            {...this.props.FirstInvolvement}
            hideDay
            minDateEqualTo
            showEstimated={false}
            onUpdate={this.updateFirstInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.involvement.heading.recentInvolvement')}
          attribute={`${attribute}.RecentInvolvement`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="RecentInvolvement"
            attribute={`${attribute}.RecentInvolvement`}
            className="recent-involvement"
            {...this.props.RecentInvolvement}
            hideDay
            prefix="drugPurchase"
            minDate={this.props.FirstInvolvement}
            minDateEqualTo
            showEstimated={false}
            onUpdate={this.updateRecentInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.involvement.heading.natureOfInvolvement')}
          attribute={`${attribute}.NatureOfInvolvement`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="NatureOfInvolvement"
            attribute={`${attribute}.NatureOfInvolvement`}
            className="nature-of-involvement"
            {...this.props.NatureOfInvolvement}
            onUpdate={this.updateNatureOfInvolvement}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.involvement.heading.reasons')}
          attribute={`${attribute}.Reasons`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Reasons"
            attribute={`${attribute}.Reasons`}
            className="reasons"
            {...this.props.Reasons}
            onUpdate={this.updateReasons}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        {requireDrugWhileSafety && (
          <Branch
            name="InvolvementWhileEmployed"
            attribute={`${attribute}.InvolvementWhileEmployed`}
            label={i18n.t('substance.drugs.involvement.heading.involvementWhileEmployed')}
            labelSize="h4"
            className="involvement-while-employed"
            {...this.props.InvolvementWhileEmployed}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateInvolvementWhileEmployed}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}

        {requireDrugWithClearance && (
          <Branch
            name="InvolvementWithClearance"
            attribute={`${attribute}.InvolvementWithClearance`}
            label={i18n.t('substance.drugs.involvement.heading.involvementWithClearance')}
            labelSize="h4"
            className="involvement-with-clearance"
            {...this.props.InvolvementWithClearance}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateInvolvementWithClearance}
            scrollIntoView={this.props.scrollIntoView}
          />
        )}

        {requireDrugInFuture && (
          <span>
            <Branch
              name="InvolvementInFuture"
              attribute={`${attribute}.InvolvementInFuture`}
              label={i18n.t('substance.drugs.involvement.heading.involvementInFuture')}
              labelSize="h4"
              className="involvement-in-future"
              {...this.props.InvolvementInFuture}
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateInvolvementInFuture}
              scrollIntoView={this.props.scrollIntoView}
            />

            <Show when={this.props.InvolvementInFuture?.value === 'Yes'}>
              <Field
                title={i18n.t('substance.drugs.involvement.heading.explanation')}
                attribute={`${attribute}.Explanation`}
                titleSize="label"
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  name="Explanation"
                  attribute={`${attribute}.Explanation`}
                  className="explanation"
                  {...this.props.Explanation}
                  onUpdate={this.updateExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
              <Comments
                title={i18n.t('substance.comments.optional')}
                name="InvolvementInFutureComments"
                className="optional-comment"
                attribute="InvolvementInFutureComments"
                {...this.props.InvolvementInFutureComments}
                onUpdate={this.updateInvolvementInFutureComments}
                required={false}
              />
            </Show>
          </span>
        )}
        <Comments
          title={i18n.t('substance.comments.summary')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

DrugInvolvement.defaultProps = {
  InvolvementWhileEmployed: {},
  InvolvementWithClearance: {},
  InvolvementInFuture: {},
  Comments: {},
  InvolvementInFutureComments: {},
  DrugTypeOther: {},
  HowManyTimes: {},
  InvolvementWhileEmployedPosition: {},
  InvolvementWhileEmployedExplanation: {},
  InvolvementWithClearanceExplanation: {},
  InvolvementInFutureExplanation: {},
  onError: (value, arr: ErrorCode[]) => arr,
};
